import { ChainFullIds, ChainIds } from "@umami/chains";

import { fetchPopularTokens } from "@/services/tokenService";

// Cache for 1inch tokens to avoid multiple API calls
let cached1inchTokens: Array<{ symbol: string; address: string }> | null = null;

const getCached1inchTokens = async (): Promise<Array<{ symbol: string; address: string }>> => {
  if (cached1inchTokens) {
    return cached1inchTokens;
  }

  try {
    const tokens = await fetchPopularTokens(ChainFullIds.ETHEREUM);

    cached1inchTokens = tokens.map(token => ({
      symbol: token.symbol,
      address: token.address || "",
    }));

    return cached1inchTokens;
  } catch (error) {
    console.error("Error fetching 1inch tokens:", error);
    cached1inchTokens = [];

    return [];
  }
};

/**
 * Check if a token is available in the 1inch API for Ethereum
 * @param symbol - The token symbol
 * @param address - The token address
 * @returns Promise<boolean> - True if the token is available in 1inch API, false otherwise
 */
export const isTokenAvailableIn1inch = async (
  symbol: string,
  address: string
): Promise<boolean> => {
  try {
    const tokens = await getCached1inchTokens();
    // Normalize addresses for comparison
    const normalizedAddress = address.toLowerCase();

    const match = tokens.some(token => {
      const symbolMatch = token.symbol === symbol;
      const tokenAddress = token.address.toLowerCase();
      const addressMatch = tokenAddress === normalizedAddress;

      return symbolMatch && addressMatch;
    });

    return match;
  } catch (error) {
    console.error("Error checking 1inch token availability:", error);

    return false;
  }
};

/**
 * Check if a token should have a buy button
 * This includes:
 * - XTZ on Tezos
 * - XTZ on Etherlink
 * - USDC on Etherlink
 * - For Ethereum: only tokens available in the 1inch API call
 * @param chainId - The chain ID of the token
 * @param symbol - The symbol of the token
 * @param address - Optional token address
 * @returns Promise<boolean> - True if the token should have a buy button, false otherwise
 */
export const shouldShowBuyButton = async (
  chainId: string,
  symbol: string,
  address?: string
): Promise<boolean> => {
  // XTZ on Tezos
  if (chainId === ChainIds.TEZOS && symbol === "XTZ") {
    return true;
  }

  // XTZ on Etherlink
  if (chainId === ChainIds.ETHERLINK && symbol === "XTZ") {
    return true;
  }

  // USDC on Etherlink
  if (chainId === ChainIds.ETHERLINK && symbol === "USDC") {
    return true;
  }

  // For Ethereum: tokens available in the 1inch API call
  if (
    chainId === ChainIds.ETHEREUM &&
    address &&
    address !== "0x0000000000000000000000000000000000000000"
  ) {
    return await isTokenAvailableIn1inch(symbol, address);
  }

  return false;
};

/**
 * Returns a user-friendly display name for a token symbol.
 * Extend this function if more tokens require custom display names.
 */
export function getTokenDisplayName(symbol?: string): string {
  if (!symbol) {
    return "Crypto";
  }
  switch (symbol) {
    case "XTZ":
      return "Tez";
    default:
      return symbol;
  }
}
